import React, { useState } from "react";
import { useSelector } from "react-redux";

import { Box, Button, Stack, Typography } from "@mui/material";

import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

import Select from "../elements/Select";
import Input from "../elements/Input";

import FileUpload from "../elements/FileUpload";
import RichTextEditorModal from "../modals/RichTextEditorModal";

// variability: {
//   dataSource: { type: String, enum: ["location", "variability"] },
//   fields: { type: String },
//   displayType: {
//     type: String,
//     enum: ["plain", "image", "html", "grid", "template"],
//   },
//   template: { type: Object },
// },

const Variability = ({
  surveyQuestionId,
  surveySectionId,
  variability,
  onChange,
}) => {
  const { addToWorkQueue } = useData();
  const { showMessage } = usePage();

  const [showHtmlEditor, setShowHtmlEditor] = useState(false);

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  const handleChange = (field, newValue) => {
    let newVariability;
    if (variability) {
      newVariability = { ...variability, [field]: newValue };
    } else {
      newVariability = { [field]: newValue };
    }

    onChange(newVariability);
  };

  const buttonsDisabled = () => {
    let disabled = true;

    if (surveyQuestionId || surveySectionId) disabled = false;
    // if (!variability?.dataSource) disabled = false;
    // if (!variability?.displayType) disabled = false;
    // if (!variability?.variabilityType) disabled = false;

    return disabled;
  };

  const handleExportTemplate = async () => {
    let details = {};
    if (surveyQuestionId) details.surveyQuestionId = surveyQuestionId;
    if (surveySectionId) details.surveySectionId = surveySectionId;
    details.variability = variability;

    await addToWorkQueue("exportVariabilityTemplate", details);

    showMessage("Your file will be emailed to you shortly.");
  };

  const handleExport = async () => {
    let details = {};
    if (surveyQuestionId) details.surveyQuestionId = surveyQuestionId;
    if (surveySectionId) details.surveySectionId = surveySectionId;

    await addToWorkQueue("exportVariability", details);

    showMessage("Your file will be emailed to you shortly.");
  };

  const handleUploaded = () => {
    showMessage("You will receive an email on completion.");
  };

  const handleHtmltemplate = () => {
    setShowHtmlEditor(true);
  };

  const handleTemplateChange = (html) => {
    handleChange("template", html);
  };

  const variabilityTypes = () => {
    if (variability?.dataSource === "variability") {
      return [
        { id: "retailer", name: "Retailer" },
        { id: "banner", name: "Banner" },
        { id: "locationId", name: "Location" },
        { id: "province", name: "Province" },
        { id: "category", name: "Category" },
        { id: "family", name: "Family" },
        { id: "sku", name: "SKU" },
        { id: "skuField", name: "SKU Field" },
      ];
    } else {
      return [{ id: "customField", name: "Custom Field" }];
    }
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        border="1px solid #ccc"
        borderRadius="5px"
        padding="10px"
      >
        <Typography textAlign="center">Variability</Typography>
        <Stack spacing={1}>
          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <Box sx={{ flex: 1 }}>
              <Select
                label="Data Source"
                value={variability?.dataSource}
                items={[
                  { id: "variability", name: "Custom Import" },
                  { id: "location", name: "Location Data" },
                ]}
                onChange={(newValue) => handleChange("dataSource", newValue)}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Select
                label="Display Type"
                value={variability?.displayType}
                items={[
                  { id: "plain", name: "Plain" },
                  { id: "image", name: "Image" },
                  { id: "grid", name: "Grid/Table" },
                  { id: "template", name: "Template" },
                ]}
                onChange={(newValue) => handleChange("displayType", newValue)}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Select
                label="Variability Type"
                value={variability?.variabilityType}
                items={variabilityTypes()}
                onChange={(newValue) =>
                  handleChange("variabilityType", newValue)
                }
              />
            </Box>
            {variability?.variabilityType === "skuField" ||
            variability?.variabilityType === "customField" ? (
              <Box sx={{ flex: 1 }}>
                <Input
                  label=" Field"
                  value={variability?.field}
                  onChange={(newValue) => handleChange("field", newValue)}
                />
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{ display: "flex" }}
            gap={1}
          >
            <FileUpload
              entityName="Variability"
              entityId={surveyQuestionId || surveySectionId}
              data={{
                workType: "importVariability",
                entityName: "Variability",
                surveySectionId,
                surveyQuestionId,
                userId: currentUserId,
                email: email,
              }}
              onUploaded={handleUploaded}
              buttonStyle={{ fontSize: "10px" }}
              disabled={buttonsDisabled()}
            />
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "10px" }}
              disabled={buttonsDisabled()}
            >
              View
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "10px" }}
              disabled={buttonsDisabled()}
              onClick={handleExportTemplate}
            >
              Template
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "10px" }}
              disabled={buttonsDisabled()}
              onClick={handleExport}
            >
              Export
            </Button>
            {variability?.displayType === "template" && (
              <Button
                size="small"
                variant="contained"
                sx={{ fontSize: "10px" }}
                disabled={buttonsDisabled()}
                onClick={handleHtmltemplate}
              >
                HTML
              </Button>
            )}
          </Box>
          {buttonsDisabled() && (
            <Typography sx={{ fontSize: "8px" }}>
              Data Source, Display Type & Variability Type fields are required
              and the question must be saved first.
            </Typography>
          )}
        </Stack>
      </Box>

      {showHtmlEditor && (
        <RichTextEditorModal
          html={variability?.template}
          open={showHtmlEditor}
          onClose={() => setShowHtmlEditor(false)}
          onChange={handleTemplateChange}
        />
      )}
    </>
  );
};

export default Variability;
